import {createSelector} from 'reselect'
import keyBy from 'lodash/keyBy.js'

import {getState, setForm, formsSelector} from '../store.js'
import api from '../common/api.js'
import {hasTransferPermissionSelector} from '../data/me.js'
import {
  canTransferInventorySelector,
  showFeatureLocksSelector,
} from '../data/company.js'
import {buildPath} from '../common/querystring.js'
import {fetchAllAPI} from '../common/fetchAll.js'
import {TRANSFER_URI_COMPONENT} from '../common/constants/Transfers.js'
import {ensureProductsLoaded} from './products.js'

export const TRANSFERS = 'transfers'

export function setTransfers(transfers) {
  setForm(TRANSFERS, keyBy(transfers, 'transfer_id'))
}

export function setTransfer(transfer) {
  const transfers = transfersSelector(getState())

  setForm(TRANSFERS, {...transfers, [transfer.transfer_id]: transfer})
}

export function updateTransfer(transferID, updates) {
  const transfer = transferSelector(getState(), {transferID})

  setTransfer({...transfer, ...updates})
}

export function removeTransfer(transferID) {
  const transfers = {...transfersSelector(getState())}

  delete transfers[transferID]

  setForm(TRANSFERS, transfers)
}

export function transfersSelector(state) {
  return formsSelector(state)[TRANSFERS] || transfersSelector.default
}
transfersSelector.default = {}

export function transferSelector(state, {transferID}) {
  return transfersSelector(state)[transferID]
}

export function transfersByTransferIDSelector(state, {transferIDs}) {
  const transfers = transfersSelector(state)

  return transferIDs.map((transferID) => transfers[transferID])
}

export const createTransferSelector = (transferID) =>
  createSelector(transfersSelector, (transfers) => transfers[transferID])

export function hasTransferFeatureSelector(state) {
  return canTransferInventorySelector(state)
}

export const canUseTransferSelector = createSelector(
  hasTransferPermissionSelector,
  hasTransferFeatureSelector,
  (hasPermission, hasFeature) => hasPermission && hasFeature,
)

export function showTransferNavSelector(state) {
  const showFeatureLocks = showFeatureLocksSelector(state)
  const hasTransferFeature = hasTransferFeatureSelector(state)
  const hasTransferPermission = hasTransferPermissionSelector(state)

  return hasTransferPermission && (hasTransferFeature || showFeatureLocks)
}

export function transferLineSelector(state, {transferID, lineID}) {
  const transfer = transferSelector(state, {transferID})

  return transfer.lines.find(({id}) => id === lineID)
}

export async function ensureTransfer(transferID, {reload} = {}) {
  let transfer = transferSelector(getState(), {transferID})

  if (!reload && transfer) {
    return transfer
  }

  const {json} = await api.get(buildPath([TRANSFER_URI_COMPONENT, transferID]))

  await ensureProductsLoaded(json.lines.map(({sku}) => sku))

  setTransfer(json)

  return json
}

export async function ensureTransferComments(transferID) {
  const transfer = transferSelector(getState(), {transferID})

  if (transfer?.comments?.length > 0) {
    return
  }

  const comments = await fetchAllAPI(
    buildPath([TRANSFER_URI_COMPONENT, transferID, 'comment']),
    'comment',
  )

  updateTransfer(transferID, {comments})
}
